/**
 * ShareToXButton — one-click X (Twitter) intent link for a row.
 *
 * Round 98l (Jul 2026). Opens https://x.com/intent/post prefilled
 * with a compact, share-ready blurb + a canonical URL back to the
 * ticker on our site (drives referral traffic).
 *
 * Kept as a small anchor tag rather than an axios call — X's
 * intent endpoint requires a browser-driven redirect, not a fetch.
 */
import { trackEvent } from '@/components/Analytics';
import { Share2 } from 'lucide-react';

const SITE_ORIGIN = 'https://twylightcrow.com';

export default function ShareToXButton({ ticker, text, hashtags = [], size = 'w-3.5 h-3.5', variant = 'inline', url: urlOverride, label = 'Share' }) {
  // Round 98m (Jul 2026) — `urlOverride` lets standalone SEO pages
  // (/founder-led, /ipo-watch) share the backend OG-unfurl URL
  // instead of the default /stock/{ticker} page. Crawlers hitting
  // the OG endpoint get rich per-page cards; the endpoint then
  // meta-refreshes real browsers to the SPA route.
  const url = urlOverride || `${SITE_ORIGIN}/stock/${encodeURIComponent(ticker)}`;
  const params = new URLSearchParams({
    text: text || `Checking out $${ticker} on TwylightCrow`,
    url,
  });
  if (hashtags.length) params.set('hashtags', hashtags.join(','));
  const href = `https://x.com/intent/post?${params.toString()}`;

  const isInline = variant === 'inline';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { e.stopPropagation(); trackEvent('share', { method: 'X', content_type: 'stock', item_id: ticker || url }); }}
      className={isInline
        ? 'inline-flex items-center justify-center p-1 rounded hover:bg-muted/40 text-muted-foreground hover:text-sky-500 transition-colors'
        : 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border border-border hover:border-sky-500/40 hover:text-sky-500 transition-colors'}
      data-testid={`share-x-${ticker}`}
      title={`Share ${ticker} on X`}
      aria-label={`Share ${ticker} on X`}
    >
      <Share2 className={size} />
      {!isInline && <span>{label}</span>}
    </a>
  );
}
